import { Injectable } from "@angular/core";

import * as Buffer from "buffer/index";
import * as CryptoJS from "crypto-js";

import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class SignatureService {
  private accountid: string;
  private accessKeyID: string;
  private accessKeySecret: string;
  private securityToken: string;
  private endpoint: string;
  private host: string;
  private version: string;

  constructor() {
    const config = environment.aliyun;

    this.accountid = config.accountid;
    this.accessKeyID = config.accessKeyID;
    this.accessKeySecret = config.accessKeySecret;

    if (!this.accountid) {
      throw new TypeError('"config.accountid" must be passed in');
    }
    if (!this.accessKeyID) {
      throw new TypeError('"config.accessKeyID" must be passed in');
    }
    if (!this.accessKeySecret) {
      throw new TypeError('"config.accessKeySecret" must be passed in');
    }

    if (this.accessKeyID.startsWith("STS")) {
      this.securityToken = config.securityToken;

      if (!this.securityToken) {
        throw new TypeError('"config.securityToken" must be passed in for STS');
      }
    }

    const region = config.region;
    if (!region) {
      throw new TypeError('"config.region" must be passed in');
    }

    const protocol = config.secure ? "https" : "http";
    const internal = config.internal ? "-internal" : "";

    this.endpoint = `${protocol}://${this.accountid}.${region}${internal}.fc.aliyuncs.com`;
    this.host = `${this.accountid}.${region}${internal}.fc.aliyuncs.com`;
    this.version = "2016-08-15";
  }

  private isJSON(str: string) {
    try {
      const obj = JSON.parse(str);

      if (typeof obj === "object" && obj) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  private buildCanonicalHeaders(headers: any, prefix: string) {
    let canonical = "";
    let list = [];
    let kv = {};
    let keys = Object.keys(headers);

    keys.forEach((key) => {
      const lowerKey = key.toLowerCase().trim();

      if (lowerKey.startsWith(prefix)) {
        list.push(lowerKey);
        kv[lowerKey] = headers[key];
      }
    });

    list.sort();
    list.forEach((key) => {
      canonical += `${key}:${kv[key]}\n`;
    });

    return canonical;
  }

  private composeStringToSign(
    method: string,
    path: string,
    headers: any,
    queries: any
  ) {
    let contentMD5 = headers["content-md5"] || "";
    let contentType = headers["content-type"] || "";
    let date = headers["x-fc-date"];
    let signHeaders = this.buildCanonicalHeaders(headers, "x-fc-");

    // let u = url.parse(path);
    let u = new URL(path, this.endpoint);
    let pathUnescaped = decodeURIComponent(u.pathname);
    let str = `${method}\n${contentMD5}\n${contentType}\n${date}\n${signHeaders}${pathUnescaped}`;

    if (queries && queries.size) {
      let params = [];

      queries.forEach((values: any, key: string) => {
        let type = typeof values;

        if (type === "string") {
          params.push(`${key}=${values}`);
          return;
        }

        if (type === "object" && values instanceof Array) {
          values.forEach((value: any) => {
            params.push(`${key}=${value}`);
          });
        }
      });

      params.sort();
      str += "\n" + params.join("\n");
    }

    return str;
  }

  private signString(source: any, secret: any) {
    // const buff = crypto
    //   .createHmac("sha256", secret)
    //   .update(source, "utf8")
    //   .digest();

    // return new Buffer(buff, "binary").toString("base64");

    return CryptoJS.HmacSHA256(source, secret).toString(CryptoJS.enc.Base64);
  }

  private buildHeaders(accept: string, body: any) {
    const now = new Date();

    const headers = {
      accept: accept || "application/json",
      "x-fc-date": now.toUTCString(),
      // host: this.host,
      "x-fc-account-id": this.accountid,
    };

    if (this.securityToken) {
      headers["x-fc-security-token"] = this.securityToken;
    }

    if (body) {
      let buff = null;

      if (Buffer.Buffer.isBuffer(body)) {
        buff = body;
        headers["content-type"] = "application/octet-stream";
      } else if (typeof body === "string") {
        if (this.isJSON(body)) {
          buff = new Buffer.Buffer(body, "utf8");
          headers["content-type"] = "application/json";
        } else {
          buff = new Buffer.Buffer(body, "utf8");
          headers["content-type"] = "application/octet-stream";
        }
      } else {
        buff = new Buffer.Buffer(JSON.stringify(body), "utf8");
        headers["content-type"] = "application/json";
      }

      // const digest = kitx.md5(buff, "hex");
      const digest = CryptoJS.MD5(buff.toString("utf8")).toString(
        CryptoJS.enc.Hex
      );
      const md5 = new Buffer.Buffer(digest, "utf8").toString("base64");

      headers["content-length"] = buff.length;
      headers["content-md5"] = md5;
    }

    return headers;
  }

  getVersion() {
    return this.version;
  }

  getHost() {
    return this.host;
  }

  getEndpoint() {
    return this.endpoint;
  }

  getProxyURL() {
    return `${this.endpoint}/${this.version}/proxy`;
  }

  buildRequestContext(
    method: string,
    path: string,
    headers: any,
    queries: any,
    body: any
  ) {
    let m = method.toUpperCase();
    let p = path.startsWith("/") ? path : `/${path}`;
    let h = headers || {};

    Object.assign(h, this.buildHeaders(h.accept, body));

    const stringToSign = this.composeStringToSign(m, p, h, queries);
    const signature = this.signString(stringToSign, this.accessKeySecret);

    h["authorization"] = `FC ${this.accessKeyID}:${signature}`;

    return h;
  }
}
